import React, { useState, useEffect } from "react";
import { TextField, MenuItem } from "@mui/material";
import "./components.css";
import { getLocalizedFieldLabel } from "../helpers/helpers";
import { leadAssignApiEndpoints } from "../constants";

const CityAutoComplete = ({ formData, onSelectionChange, label, lang }) => {
  const [city, setCity] = useState(formData.city || "");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCityData, setSelectedCityData] = useState(
    formData.city
      ? {
          city: formData.city,
          state: formData.state,
          county: formData.county,
          zip: formData.zipCode,
        }
      : null
  );

  useEffect(() => {
    if (!city || city.trim().length === 0) {
      setSuggestions([]);
      return;
    }

    const trimmedCity = city.trim();
    if (trimmedCity.length < 2) {
      setSuggestions([]);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(
          leadAssignApiEndpoints.CITES_ENDPOINT,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ input: trimmedCity }),
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch city suggestions");
        }

        const data = await response.json();
        setSuggestions(data?.predictions || []);
      } catch (error) {
        if (error?.name === "AbortError") return;
        console.error("Error fetching city suggestions:", error);
        setSuggestions([]);
      }
    }, 400);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [city]);

  const handleSelection = (suggestion) => {
    // Extract city from structured_formatting.main_text or terms[0].value
    const cityName = suggestion?.structured_formatting?.main_text || suggestion?.terms?.[0]?.value || "";
    
    // Extract state from terms[1].value (second term)
    const state = suggestion?.terms?.[1]?.value || "";
    
    // Set county and zip to empty strings (not available in cities API response)
    const selectedCityDetails = {
      city: cityName,
      state: state,
      county: "",
      zip: "",
    };

    setCity(cityName);
    setSelectedCityData(selectedCityDetails);
    onSelectionChange(selectedCityDetails, cityName);
    setSuggestions([]);
  };

  return (
    <div style={{ width: "100%"}}>
      <TextField
        label={getLocalizedFieldLabel("city", lang) || label}
        value={city}
        onChange={(e) => {
          const newValue = e.target.value;
          setCity(newValue);
          // Clear selection when user types to allow re-searching
          if (selectedCityData && newValue !== selectedCityData.city) {
            setSelectedCityData(null);
          }
        }}
        className="flexAlignColumnClassName"
        InputLabelProps={{
          classes: {
            root: "outlinedInputLabel",
            focused: "focusedInputLabel",
          },
        }}
      />
      {suggestions.length > 0 && (
        <div style={{ border: "1px solid #ccc", borderRadius: "4px" }}>
          {suggestions.map((suggestion) => {
            const cityName = suggestion?.structured_formatting?.main_text || suggestion?.terms?.[0]?.value || "";
            const state = suggestion?.terms?.[1]?.value || "";
            return (
              <MenuItem
                key={suggestion.place_id}
                onClick={() => handleSelection(suggestion)}
              >
                {cityName}{state ? `, ${state}` : ""}
              </MenuItem>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CityAutoComplete;
